import api from "./apiService";

let pendingUserRequest;
let pendingUserToken;

export const fetchUser = () => {
  const token = localStorage.getItem("token");
  if (pendingUserRequest && pendingUserToken === token) return pendingUserRequest;
  pendingUserToken = token;
  const request = api.post("/user/fetch-user").then((response) => {
    const data = response.data;
    if (data?.data && token && localStorage.getItem("token") === token) {
      let storedUser = {};
      try { storedUser = JSON.parse(localStorage.getItem("user") || "{}"); } catch { /* Replace invalid cache. */ }
      localStorage.setItem("user", JSON.stringify({
        ...storedUser,
        first_name: data.data.first_name ?? storedUser.first_name ?? "",
        last_name: data.data.last_name ?? storedUser.last_name ?? "",
        profile_photo: data.data.profile_photo || "",
        personal_email: data.data.personal_email ?? storedUser.personal_email ?? "",
      }));
      window.dispatchEvent(new Event("user-updated"));
    }
    return data;
  }).finally(() => {
    if (pendingUserRequest === request) pendingUserRequest = null;
  });
  pendingUserRequest = request;
  return request;
};

export const updateUser = async (payload) => {
  const response = await api.post("/user/update-user", payload);
  return response.data;
};

export const fetchTwoFAStatus = async () => {
  const response = await api.get("/user/2fa/status");
  return response.data;
};

export const startTwoFASetup = async (payload) => {
  const response = await api.post("/user/2fa/setup", payload);
  return response.data;
};

export const resendTwoFACode = async (payload) => {
  const response = await api.post("/user/2fa/resend", payload);
  return response.data;
};

export const fetchLoginActivity = async () => {
  const response = await api.get("/user/login-activity");
  return response.data;
};

export const fetchActiveDevices = async () => {
  const response = await api.get("/user/active-devices");
  return response.data;
};

export const logoutDevice = async (sessionId) => {
  const response = await api.post("/user/logout-device", { session_id: sessionId });
  return response.data;
};

export const logoutAllDevices = async () => {
  const response = await api.post("/user/logout-all-devices");
  return response.data;
};

export const verifyTwoFA = async (payload) => {
  const response = await api.post("/user/2fa/verify", payload);
  return response.data;
};

export const requestDisableTwoFACode = async () => {
  const response = await api.post("/user/2fa/disable_2fa_otp");
  if (response.data?.success === false) throw new Error(response.data.message || "Unable to send verification code.");
  return response.data;
};

export const disableTwoFA = async (payload) => {
  const response = await api.post("/user/2fa/disable_2fa", payload);
  if (response.data?.success !== true) throw new Error(response.data?.message || "Unable to disable two-factor authentication.");
  return response.data;
};

export const changePassword = async (payload) => {
  const response = await api.post("/user/change-password", payload);
  return response.data;
};

export const fetchNotificationSettings = async () => {
  const response = await api.get("/notifications/settings");
  return response.data;
};

export const updateNotificationSettings = async (type, enabled, pushSubscription) => {
  const payload = {
    type,
    enabled: enabled ? 1 : 0,
  };

  if (enabled && pushSubscription) {
    payload.push_subscription = {
      endpoint: pushSubscription.endpoint,
      keys: {
        p256dh: pushSubscription.keys?.p256dh,
        auth: pushSubscription.keys?.auth,
      },
    };
  }

  const response = await api.put("/notifications/settings", payload);
  return response.data;
};

export const createSupportRequest = async (message, attachment) => {
  const formData = new FormData();
  formData.append("message", message.trim());

  if (attachment) {
    formData.append("attachment", attachment);
  }

  const response = await api.post("/support/create", formData, {
    // Let the browser add the multipart boundary to the Content-Type header.
    headers: { "Content-Type": undefined },
    timeout: 60000,
  });

  if (!response?.data || response.data.success === false) {
    throw new Error(response?.data?.message || "Unable to submit your support request.");
  }

  return response.data;
};

export const uploadProfilePhoto = async (file, memberId) => {
  const formData = new FormData();
  formData.append("profile_photo", file);
  formData.append("member_id", memberId);

  // The shared `api` instance defaults Content-Type to application/json.
  // Explicitly forcing it to "multipart/form-data" here (without a boundary)
  // stops axios from letting the browser auto-generate and attach the
  // boundary a multipart body actually needs, so the server can't parse the
  // file out of the request. Setting it to undefined removes the override
  // and lets axios/the browser set the full "multipart/form-data;
  // boundary=..." header itself once it sees the body is a FormData.
  const response = await api.post("/user/upload-profile-photo", formData, {
    headers: { "Content-Type": undefined },
    timeout: 60000,
  });
  if (!response?.data || response.data.success === false) {
    throw new Error(response?.data?.message || "Profile photo upload failed. Please try again.");
  }
  return response.data;
};
